import React, { useEffect, useRef, useState } from "react";
import { Input, Textarea } from "@mui/joy";
import { debounce } from "lodash";

// 타이핑이 멈춘 뒤 모델(blocks)로 반영하기까지의 지연.
// 이 시간 안의 연속 입력은 1번의 커밋으로 합쳐진다. 짧으면 무거운 리렌더가 잦아지고,
// 길면 저장/미리보기 반영이 늦게 느껴진다 — 그 절충값.
const COMMIT_DEBOUNCE_MS = 300;

type TaskEditorTextFieldProps = {
  // 모델(blocks)이 들고 있는 현재 값. 편집을 시작하면 내부 draft 가 우선한다.
  value: string;
  // 값을 모델에 반영하는 콜백. 키 입력마다가 아니라 debounce/blur 시점에만 호출된다.
  // (내부적으로 commitTextEdit → setBlocks 로 이어지는 "무거운" 경로)
  onCommit: (value: string) => void;
  // 기존 onTextEditBlur 패스스루(= undo 그룹 리셋). 값 flush "이후" 에 호출된다.
  onBlur?: () => void;
  // true → <Textarea>(여러 줄), 미지정 → <Input>(한 줄).
  multiline?: boolean;
  minRows?: number;
  placeholder?: string;
  // MUI Joy 스타일 패스스루 — 호출부가 기존과 동일하게 넘긴다.
  size?: React.ComponentProps<typeof Input>["size"];
  variant?: React.ComponentProps<typeof Input>["variant"];
  sx?: React.ComponentProps<typeof Input>["sx"];
  // 기존 테스트/식별용 testid 보존을 위해 내부 인풋으로 forward.
  "data-testid"?: string;
};

/**
 * 타이핑을 모델(blocks)에서 분리하는 leaf 텍스트 인풋.
 *
 * 배경: 활동지 에디터의 인풋은 워크스페이스 전역 `blocks` state 에 묶인 controlled 인풋이라,
 * 글자 하나 칠 때마다 setBlocks → 거대한 TaskEditorWorkspace 가 (그것도 2번) 리렌더됐다.
 * 게다가 한글 IME 조합 처리가 없어 조합 중 리렌더가 끼어들며 조합이 끊겼다 → 타이핑 버벅임.
 *
 * 해결: 이 컴포넌트가 자기 `draft` 로컬 state 로 인풋을 구동한다.
 *  - 키 입력 = setDraft → "이 작은 컴포넌트만" 리렌더(워크스페이스 X). 글자는 즉시 표시.
 *  - 무거운 onCommit(→ setBlocks → 워크스페이스/페이지/채팅 리렌더)은 debounce·blur 에 1회만.
 *  - 한글 IME 조합 중에는 커밋을 보류 → 조합 끊김/버벅임 방지.
 *
 * 패턴 출처: components/task/content/cell/textarea/PlainTextarea.tsx
 * (로컬 state + 조합 인지 + blur flush + !focus 일 때만 외부값 동기화).
 */
export default function TaskEditorTextField({
  value,
  onCommit,
  onBlur,
  multiline,
  minRows,
  placeholder,
  size,
  variant,
  sx,
  "data-testid": testId,
}: TaskEditorTextFieldProps) {
  // 편집 중에는 draft 가 화면의 단일 진실. 모델(value)은 debounce 후에야 따라온다.
  const [draft, setDraft] = useState(value);

  // blur/언마운트 콜백에서 "가장 최신 draft" 를 stale 클로저 없이 읽기 위한 거울.
  const draftRef = useRef(draft);
  draftRef.current = draft;

  // 포커스 여부(외부값 동기화 게이트) / IME 조합 여부(커밋 보류 게이트).
  // 렌더에 영향을 줄 필요가 없어 state 가 아닌 ref 로 둔다.
  const focusedRef = useRef(false);
  const composingRef = useRef(false);

  // debounce 클로저는 1번만 생성되므로(아래), 그 안에서 항상 "최신" onCommit 을 호출하도록 ref 로 노출.
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  // 외부(agent patch·undo·discard·블록 reorder)에서 value 가 바뀌면 draft 를 동기화.
  // 단, 편집 중(focus)에는 덮어쓰지 않아 사용자가 치던 입력을 보존한다.
  // (사용자가 만지던 블록의 agent patch 는 상위 3-way merge 에서 conflict 로 보류되므로 충돌 위험도 작다.)
  useEffect(() => {
    if (!focusedRef.current) {
      setDraft(value);
    }
  }, [value]);

  // debounce 인스턴스는 마운트 동안 단 1개만 유지(매 렌더 재생성 시 타이머가 리셋되어 영영 안 터질 수 있음).
  const debouncedCommit = useRef(
    debounce((next: string) => onCommitRef.current(next), COMMIT_DEBOUNCE_MS)
  ).current;

  // 언마운트 시 대기 중인 커밋은 flush 가 아니라 "취소" 한다.
  // 블록 삭제 등으로 이 인풋이 사라지는 경우, 이미 없어진 위치로 값을 쓰면 안 되기 때문.
  // 일반적인 이탈(blur·탭 전환·저장 클릭)은 blur 가 먼저 일어나 값이 이미 커밋되어 유실이 없다.
  useEffect(() => () => debouncedCommit.cancel(), [debouncedCommit]);

  const handleChangeValue = (next: string) => {
    setDraft(next); // 즉시 화면 반영(가벼움)
    // 조합 중에는 모델 커밋 보류 → compositionEnd 에서 한 번에 반영(조합 끊김 방지).
    if (!composingRef.current) {
      debouncedCommit(next);
    }
  };

  const handleFocus = () => {
    focusedRef.current = true;
  };

  const handleBlur = () => {
    // 떠나기 전 마지막 값을 즉시 반영: 대기 중 debounce 를 취소하고 현재 draft 를 커밋.
    debouncedCommit.cancel();
    onCommitRef.current(draftRef.current);
    focusedRef.current = false;
    // 값 커밋 "후" 에 기존 blur 처리(undo 그룹 리셋)를 호출 — 순서 중요.
    onBlur?.();
  };

  const handleCompositionStart = () => {
    composingRef.current = true;
  };

  // Textarea(textarea 루트)와 Input(div 루트)의 슬롯 타입이 달라 공용 props 스프레드 대신
  // 분기별로 핸들러를 직접 단다. compositionEnd 의 e.target 은 SyntheticEvent 기준 EventTarget 이라
  // 실제 엘리먼트로 캐스팅해 값을 읽는다.
  if (multiline) {
    return (
      <Textarea
        minRows={minRows}
        placeholder={placeholder}
        value={draft}
        size={size}
        variant={variant}
        sx={sx}
        data-testid={testId}
        onChange={(e) => handleChangeValue(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={(e) => {
          // 조합 완료 → 보류했던 커밋을 여기서 예약.
          composingRef.current = false;
          const next = (e.target as HTMLTextAreaElement).value;
          setDraft(next);
          debouncedCommit(next);
        }}
      />
    );
  }

  return (
    <Input
      placeholder={placeholder}
      value={draft}
      size={size}
      variant={variant}
      sx={sx}
      data-testid={testId}
      onChange={(e) => handleChangeValue(e.target.value)}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={(e) => {
        // 조합 완료 → 보류했던 커밋을 여기서 예약.
        composingRef.current = false;
        const next = (e.target as HTMLInputElement).value;
        setDraft(next);
        debouncedCommit(next);
      }}
    />
  );
}
